import type { MetaNodeDef } from '../../game/meta';
import { maxConstraints } from '../../game/meta';
import { Constraint } from '../../sim/constraints';

/* ═══════════════════════════════════════════════════════════════════════════
   LES CONTRAINTES VOLONTAIRES — le catalogue.

   Quatre options qui rendent la run plus difficile et la paient en crédits, et
   un cinquième nœud qui vend le droit d'en cumuler deux. C'est le sas
   anti-grind du §6.9 : un joueur qui a tout acheté et veut continuer à
   progresser a désormais quelque chose à faire, et ce quelque chose le rend
   MEILLEUR au lieu de rendre son vaisseau plus fort.

   ── CE QUE CE MODULE EST, ET CE QU'IL N'EST PAS ──

   Il ne contient AUCUN mécanisme. La moitié qui change le déroulement d'une
   partie vit dans `src/sim/constraints.ts` (masque de bits, cadence, plafond de
   vies) ; la moitié qui décide du tarif vit dans `src/game/meta.ts`
   (`creditMultiplier`, son plafond). Ici on ne trouve que la table qui relie
   les trois : un identifiant, un bit, un bonus, un texte.

   ⚠️ L'`id` est l'identifiant EXACT du champ `grants` du nœud de l'arbre. Même
   couplage que pour les armes secondaires, et pour la même raison : une table
   de correspondance en plus serait un troisième endroit où se tromper, et la
   faute serait muette — un nœud acheté qui n'active rien.

   Ce module est un module ORDINAIRE, pas un `*.def.ts` : il n'entre pas dans
   `DefIndex` et le registre ne le ramasse pas. C'est délibéré. La simulation
   ne lit jamais qu'un entier (`run.constraints`), et lui faire traverser
   l'index de contenu pour finir converti en masque de toute façon n'aurait
   ajouté qu'une indirection et deux champs à maintenir dans `EMPTY_INDEX`.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface ConstraintDef {
  /** Identifiant EXACT du `grants` du nœud de l'arbre. */
  readonly id: string;
  readonly name: string;
  /** Texte joueur. Chiffré, comme celui d'une carte : le joueur doit pouvoir
   *  comparer deux contraintes avant d'en choisir une. */
  readonly text: string;
  /** Bit correspondant dans `run.constraints`. */
  readonly bit: number;
  /** Majoration de crédits, en fraction. 0,45 = « +45 % de crédits ».
   *
   *  ⚠️ C'est la valeur ANNONCÉE dans le texte du nœud, et le fait qu'elle le
   *  reste après composition n'est pas un hasard : `creditMultiplier` somme les
   *  bonus au lieu de multiplier les facteurs, précisément pour que « +45 % »
   *  veuille dire +45 points de pourcentage quelle que soit l'autre contrainte
   *  emportée. Voir la justification complète dans `src/game/meta.ts`. */
  readonly creditBonus: number;
}

/* Ordre d'affichage : par difficulté croissante, qui est aussi l'ordre des
   prix et l'ordre des gains. Un catalogue trié par sévérité se lit sans lire —
   le joueur sait où regarder selon ce qu'il cherche. */
export const CONSTRAINTS: readonly ConstraintDef[] = [
  {
    id: 'contrainte-amnesie',
    name: 'Amnésie',
    text: 'Les cartes n’affichent plus leur effet chiffré ni leur delta de DPS. +30 % de crédits.',
    bit: Constraint.Amnesie,
    creditBonus: 0.3,
  },
  {
    id: 'contrainte-zero-achat',
    name: 'Zéro achat',
    text: 'La boutique de bord reste fermée pour toute la run. +35 % de crédits.',
    bit: Constraint.ZeroAchat,
    creditBonus: 0.35,
  },
  {
    id: 'contrainte-maree-noire',
    name: 'Marée noire',
    text: '+35 % de cadence de tir ennemie. +45 % de crédits.',
    bit: Constraint.MareeNoire,
    creditBonus: 0.45,
  },
  {
    id: 'contrainte-coque-de-verre',
    name: 'Coque de verre',
    text: 'Une seule vie, et aucun gain de vie de la run. +80 % de crédits.',
    bit: Constraint.CoqueDeVerre,
    creditBonus: 0.8,
  },
];

/** Le `grants` du nœud « Double contrainte ». */
export const CUMUL_GRANT = 'cumul-contraintes';

/** Préfixe de famille, fixé par l'en-tête de `tree.def.ts`. Il sert au contrôle
 *  de complétude plus bas : sans convention de nommage, on ne peut pas
 *  distinguer un nœud de contrainte d'un nœud de confort. */
const CONSTRAINT_PREFIX = 'contrainte-';

export function constraintByGrant(id: string): ConstraintDef | undefined {
  return CONSTRAINTS.find((c) => c.id === id);
}

/** Nœuds OPTION effectivement achetés, accordés par l'arbre. */
function grantsOf(nodes: readonly MetaNodeDef[], bought: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  for (const n of nodes) {
    if (n.kind !== 'option' || !bought.has(n.id)) continue;
    out.add(n.grants);
  }
  return out;
}

/** Contraintes que le joueur peut emporter.
 *
 *  Aucune n'est offerte, contrairement à la secondaire par défaut : une
 *  contrainte non achetée ne doit pas être proposée, sinon le joueur se rend la
 *  vie difficile sans en être payé. */
export function unlockedConstraints(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
): ConstraintDef[] {
  const accordes = grantsOf(nodes, bought);
  return CONSTRAINTS.filter((c) => accordes.has(c.id));
}

export function cumulUnlocked(nodes: readonly MetaNodeDef[], bought: ReadonlySet<string>): boolean {
  return grantsOf(nodes, bought).has(CUMUL_GRANT);
}

/**
 * Contraintes RÉELLEMENT applicables à la run qui démarre.
 *
 * ⚠️ Le filtrage est ici et non dans l'interface. Une sélection mémorisée en
 * sauvegarde peut désigner une contrainte dont le nœud n'est plus là, ou deux
 * contraintes alors que « Double contrainte » n'a jamais été acheté. Laisser
 * l'écran de briefing seul juge, c'est accepter que l'économie dépende d'un
 * composant d'affichage — et un joueur qui empile quatre contraintes en
 * bricolant sa sauvegarde ne doit pas se faire payer davantage.
 *
 * La troncature garde l'ordre de `CONSTRAINTS`, donc les MOINS payantes : le
 * dépassement se résout au détriment du joueur, jamais en sa faveur.
 */
export function resolveConstraints(
  choisies: readonly string[],
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
): ConstraintDef[] {
  const dispo = unlockedConstraints(nodes, bought);
  const voulues = new Set(choisies);
  const retenues = dispo.filter((c) => voulues.has(c.id));
  const limite = maxConstraints(cumulUnlocked(nodes, bought));
  return retenues.length > limite ? retenues.slice(0, limite) : retenues;
}

/** Masque à passer à `World.armPlayer`. */
export function constraintMask(list: readonly ConstraintDef[]): number {
  let mask = Constraint.None as number;
  for (const c of list) mask |= c.bit;
  return mask;
}

/** Bonus à passer à `creditsForRun`. */
export function constraintBonuses(list: readonly ConstraintDef[]): number[] {
  return list.map((c) => c.creditBonus);
}

/**
 * Contraintes que l'arbre VEND et que ce module n'implémente pas.
 *
 * ⚠️ Le contrôle va dans ce sens-là, et c'est le sens utile. Un nœud à
 * 500 crédits dont le `grants` ne désigne aucune contrainte réelle s'affiche
 * « ACQUIS » et ne fait rien — la panne exacte qui a motivé tout ce chantier, et
 * la seule des deux erreurs qui coûte quelque chose au joueur. Le sens inverse
 * (une contrainte implémentée qu'aucun nœud n'ouvre) est couvert plus bas.
 *
 * Le préfixe de famille sert de critère parce que l'arbre le garantit dans son
 * en-tête. Sans lui, il faudrait deviner qu'un nœud « unlock » est une
 * contrainte plutôt qu'un mode d'entraînement.
 */
export function unimplementedConstraints(nodes: readonly MetaNodeDef[]): string[] {
  const connus = new Set(CONSTRAINTS.map((c) => c.id));
  const out: string[] = [];
  for (const n of nodes) {
    if (n.kind !== 'option') continue;
    if (!n.grants.startsWith(CONSTRAINT_PREFIX)) continue;
    if (!connus.has(n.grants)) out.push(n.grants);
  }
  return out;
}

/** Contraintes implémentées qu'AUCUN nœud n'ouvre : du contenu écrit, réglé et
 *  invisible à jamais. */
export function unreachableConstraints(nodes: readonly MetaNodeDef[]): string[] {
  const accordes = new Set<string>();
  for (const n of nodes) if (n.kind === 'option') accordes.add(n.grants);
  return CONSTRAINTS.filter((c) => !accordes.has(c.id)).map((c) => c.id);
}
